// Shallow clone a line
const cloneLineShallow = (line: any) => {
  return {
    ...line,
    id: line.id,
    points: Array.isArray(line.points) ? [...line.points] : [],
    anchor: Array.isArray(line.anchor) ? [...line.anchor] : []
  }
}

// Clone an anchor object
const cloneAnchorWithNewId = (anchor: any) => {
  if (!anchor) return null
  return { ...anchor }
}

// Create a fresh straight line between two sign centers
const freshStraightLine = (
  fromSign: any,
  toSign: any,
  addNewRouteLine: () => any,
  calculateImgCenter: (img: any) => any,
) => {
  const l = addNewRouteLine()
  l.points = [
    ...calculateImgCenter(fromSign),
    ...calculateImgCenter(toSign)
  ]
  return l
}

// Build merged line using startPair from startLine and endPair from endLine
const buildMergedLineFromEdges = (
  startLine: any | null,
  endLine: any | null,
  fromSign: any,
  toSign: any,
  addNewRouteLine: () => any,
  calculateImgCenter: (img: any) => any,
) => {
  if (startLine && Array.isArray(startLine.points) && startLine.points.length >= 2
    && endLine && Array.isArray(endLine.points) && endLine.points.length >= 2) {
    const newLine = cloneLineShallow(startLine)
    const startPair = startLine.points.slice(0, 2)
    const endPair = endLine.points.slice(endLine.points.length - 2)
    newLine.points = [
      ...startPair,
      ...endPair
    ]
    newLine.anchor = []
    return newLine
  }

  if (startLine && Array.isArray(startLine.points) && startLine.points.length >= 2) {
    const newLine = cloneLineShallow(startLine)
    const startPair = startLine.points.slice(0, 2)
    const toCenter = calculateImgCenter(toSign)
    newLine.points = [
      ...startPair,
      ...toCenter
    ]
    newLine.anchor = []
    return newLine
  }

  if (endLine && Array.isArray(endLine.points) && endLine.points.length >= 2) {
    const newLine = cloneLineShallow(endLine)
    const endPair = endLine.points.slice(endLine.points.length - 2)
    const fromCenter = calculateImgCenter(fromSign)
    newLine.points = [
      ...fromCenter,
      ...endPair
    ]
    newLine.anchor = []
    return newLine
  }

  return freshStraightLine(fromSign, toSign, addNewRouteLine, calculateImgCenter)
}

// Reuse an unaffected line: clone line and clone anchors that belong to it
const reuseUnaffectedLineWithAnchors = (oldLine: any, anchorsForLine: any[], usedAnchorObjects: Set<any>) => {
  const clonedLine = cloneLineShallow(oldLine)

  const items: any[] = []
  items.push(clonedLine)

  if (Array.isArray(anchorsForLine) && anchorsForLine.length > 0) {
    anchorsForLine.forEach(anchorObj => {
      if (!anchorObj) return
      // avoid duplicating the same original anchor object
      if (!usedAnchorObjects.has(anchorObj)) {
        const clonedAnchor = cloneAnchorWithNewId(anchorObj)
        if (clonedAnchor) {
          items.push(clonedAnchor)
          usedAnchorObjects.add(anchorObj)
        }
      }
    })
  }

  return items
}

// Extract anchor id from line.anchor
const normalizeAnchorIdRef = (ref: any) => {
  if (!ref) return null
  if (typeof ref === 'string' || typeof ref === 'number') return String(ref)
  if (typeof ref === 'object' && (ref.id || ref._id)) return String(ref.id || ref._id)
  return null
}

// Get route line object by oldLineIndex (index among sign-to-sign connections)
const routeLineObjAt = (oldLineIndex: number, routeLinesMeta: any) => {
  if (oldLineIndex < 0) return null
  if (oldLineIndex >= routeLinesMeta.length) return null
  return routeLinesMeta[oldLineIndex].lineObj
}

// Handles WF Route deletions only while preserving line order and geometry
export const handleWFRouteDeletions = (
  route: any,
  oldSignsOrder: any[],
  newSignsOrder: any[],
  deletedIndexes: number[],
  addNewRouteLine: () => any,
  calculateImgCenter: (img: any) => any,
  updateDrawingWithRouteItems: () => void
) => {
  // Sort deleted indexes ascending
  const sortedDeletedIndexes = [...deletedIndexes].sort((a, b) => a - b)

  // Use the current route.lines snapshot as authoritative (may contain lines + anchors interleaved)
  const originalRouteItems = Array.isArray(route.lines) ? [...route.lines] : []

  // Build a map anchorId -> anchorObject for direct lookup (first-pass)
  const anchorIdToObject = new Map<string, any>()
  originalRouteItems.forEach(item => {
    if (item && item.tag === 'anchor' && item.id) {
      anchorIdToObject.set(String(item.id), item)
    }
  })

  // Build routeLines array preserving order and original index positions
  const routeLinesMeta: { lineObj: any, originalIndex: number }[] = []
  originalRouteItems.forEach((item, idx) => {
    if (item && item.type === 'line' && item.tag === 'route') {
      routeLinesMeta.push({ lineObj: item, originalIndex: idx })
    }
  })

  // For each route line object determine its anchor object list in the current time
  const anchorsForLineMap = new Map<any, any[]>()
  routeLinesMeta.forEach(({ lineObj, originalIndex }) => {
    const anchorsForLine: any[] = []

    // If lineObj.anchor exists and contains references, use those to find anchor objects
    if (Array.isArray(lineObj.anchor) && lineObj.anchor.length > 0) {
      lineObj.anchor.forEach((ref: any) => {
        const aid = normalizeAnchorIdRef(ref)
        if (!aid) return
        const anchorObj = anchorIdToObject.get(aid)
        if (anchorObj) anchorsForLine.push(anchorObj)
      })
    }

    // Fallback: if no anchors were found via refs, scan following items in originalRouteItems until next route line
    if (anchorsForLine.length === 0) {
      for (let k = originalIndex + 1; k < originalRouteItems.length; k++) {
        const next = originalRouteItems[k]
        if (next && next.type === 'line' && next.tag === 'route') break
        if (next && next.tag === 'anchor') anchorsForLine.push(next)
      }
    }

    anchorsForLineMap.set(lineObj, anchorsForLine)
  })

  // Determine affected line indexes (a line is affected if either of its connected signs was deleted)
  const affectedLineIndexes = new Set<number>()
  sortedDeletedIndexes.forEach(di => {
    if (di > 0) affectedLineIndexes.add(di - 1)
    if (di < oldSignsOrder.length - 1) affectedLineIndexes.add(di)
  })

  // Track which original anchor objects we've already cloned/added in this run
  const usedAnchorObjects = new Set<any>()

  // Prepare result array
  const newRouteItems: any[] = []

  // Iterate over segments in the new signs order and either reuse or build lines
  for (let newSeg = 0; newSeg < newSignsOrder.length - 1; newSeg++) {
    const fromSign = newSignsOrder[newSeg]
    const toSign = newSignsOrder[newSeg + 1]

    const fromOldIndex = oldSignsOrder.findIndex(s => s.id === fromSign.id)
    const toOldIndex = oldSignsOrder.findIndex(s => s.id === toSign.id)

    const wereConsecutive = fromOldIndex >= 0 && toOldIndex >= 0 && Math.abs(toOldIndex - fromOldIndex) === 1

    if (wereConsecutive) {
      const oldLineIndex = Math.min(fromOldIndex, toOldIndex)
      const oldLineObj = routeLineObjAt(oldLineIndex, routeLinesMeta)

      if (oldLineObj && !affectedLineIndexes.has(oldLineIndex)) {
        // Unaffected: reuse line and its anchors (anchors determined by anchorForLineMap using refs or position)
        const anchorsForLine = anchorsForLineMap.get(oldLineObj) || []
        const items = reuseUnaffectedLineWithAnchors(oldLineObj, anchorsForLine, usedAnchorObjects)
        items.forEach(it => newRouteItems.push(it))
      } else if (oldLineObj) {
        // The old line existed but was affected (shouldn't happen often in consecutive case) -> rebuild fresh straight line
        const rebuilt = freshStraightLine(fromSign, toSign, addNewRouteLine, calculateImgCenter)
        newRouteItems.push(rebuilt)
      } else {
        // No old line found -> fresh line
        const fresh = freshStraightLine(fromSign, toSign, addNewRouteLine, calculateImgCenter)
        newRouteItems.push(fresh)
      }
    } else {
      // Non-consecutive in old order => this segment spans deleted signs -> merged line
      // Determine numeric span in old indices
      const startIdxCandidate = Math.min(
        fromOldIndex >= 0 ? fromOldIndex : Number.POSITIVE_INFINITY,
        toOldIndex >= 0 ? toOldIndex : Number.POSITIVE_INFINITY
      )
      const startIdx = isFinite(startIdxCandidate) ? startIdxCandidate : -1
      const endIdx = Math.max(fromOldIndex, toOldIndex)

      const firstAffectedLineIndex = startIdx
      const lastAffectedLineIndex = endIdx - 1

      const startLineObj = routeLineObjAt(firstAffectedLineIndex, routeLinesMeta)
      const endLineObj = routeLineObjAt(lastAffectedLineIndex, routeLinesMeta)

      const mergedLine = buildMergedLineFromEdges(startLineObj, endLineObj, fromSign, toSign, addNewRouteLine, calculateImgCenter)
      newRouteItems.push(mergedLine)
    }
  }

  // Handle empty signs order
  if (newSignsOrder.length === 0) {
    const baseRouteLine = addNewRouteLine()
    route.lines = [baseRouteLine]
    route.signsOrder = []
    updateDrawingWithRouteItems()
    return
  }

  // Handle single sign
  if (newSignsOrder.length === 1) {
    const baseRouteLine = addNewRouteLine()
    baseRouteLine.points = calculateImgCenter(newSignsOrder[0])
    route.lines = [baseRouteLine]
    route.signsOrder = newSignsOrder
    updateDrawingWithRouteItems()
    return
  }

  // Save route and update drawing
  route.signsOrder = newSignsOrder
  route.lines = newRouteItems

  updateDrawingWithRouteItems()
}
